/**
 * Personalization engine — pure function that resolves a profile into
 * deterministic defaults for the 5 v1 personalization surfaces.
 *
 * See specs/onboarding-personalization-v1.md (shipped 2026-04-21) for the
 * full design and src/data/personalizationMap.js for the 90 authored cells.
 *
 * Usage:
 *   const p = computePersonalization(profile)
 *   if (p.isEnabled) { ... use p.defaultChatTab, p.starterBibleBook, etc. ... }
 */
import {
  LEARN_PATH_MAP,
  CHAT_TAB_MAP,
  BIBLE_BOOK_MAP,
  HOME_ORDER_MAP,
  COMPLETION_CARD_MAP,
} from '@/data/personalizationMap'

// Slug → display title for the 9 paths referenced by LEARN_PATH_MAP.
// Mirrors learn_paths seed rows; kept static because personalization runs
// synchronously off the profile and the slug set is fixed. When a new
// recommendable path is added, update this map + the seed + LEARN_PATH_MAP
// in the same commit.
const LEARN_PATH_TITLES = {
  'christianity-101': 'Christianity 101',
  'how-to-read-the-bible': 'How to Read the Bible',
  'core-beliefs': 'Core Beliefs Explained',
  'walk-through-genesis': 'Walk Through Genesis',
  'understanding-john': 'Understanding John',
  'understanding-romans': 'Understanding Romans',
  'defending-the-resurrection': 'Defending the Resurrection',
  'problem-of-evil': 'The Problem of Evil',
  'science-and-faith': 'Science & Faith',
}

// Fallback goal used when a user has set role but not primary_goal. This
// matches the spec's "role-only fallback" guidance (simpler than a separate
// role-only layer).
const FALLBACK_GOAL = 'deepen_faith'

const DISABLED_RESULT = Object.freeze({
  isEnabled: false,
  recommendedLearnPath: null,
  defaultChatTab: null,
  starterBibleBook: null,
  homeDashboardOrder: null,
  completionCard: null,
})

/**
 * Look up a Learn path title from its slug. Returns null if the slug
 * isn't in the Learn catalog (defensive — should never happen in practice
 * because personalizationMap.js references real slugs).
 */
export function getLearnPathTitle(slug) {
  if (!slug) return null
  return LEARN_PATH_TITLES[slug] || null
}

/**
 * Compute the personalization result for a profile.
 *
 * Fallback logic:
 *   - `personalize_experience === false` → strict opt-out, disabled
 *   - `role` is null / empty → disabled (caller shows the "finish profile" nudge)
 *   - `role` is unknown (not in the maps) → disabled
 *   - `role` set but `primary_goal` empty → use FALLBACK_GOAL ('deepen_faith')
 */
export function computePersonalization(profile) {
  if (!profile) return DISABLED_RESULT

  // Strict opt-out — honored everywhere, no exceptions.
  if (profile.personalizeExperience === false) return DISABLED_RESULT

  const role = profile.role
  if (!role) return DISABLED_RESULT

  // Unknown role enum value — treat as disabled.
  if (!CHAT_TAB_MAP[role]) return DISABLED_RESULT

  const goal = profile.primaryGoal || FALLBACK_GOAL
  const learnSlug = LEARN_PATH_MAP[role]?.[goal] || LEARN_PATH_MAP[role]?.[FALLBACK_GOAL] || null
  const completion =
    COMPLETION_CARD_MAP[role]?.[goal] || COMPLETION_CARD_MAP[role]?.[FALLBACK_GOAL] || null

  return {
    isEnabled: true,
    recommendedLearnPath: learnSlug
      ? { slug: learnSlug, title: getLearnPathTitle(learnSlug) }
      : null,
    defaultChatTab: CHAT_TAB_MAP[role] || null,
    starterBibleBook: BIBLE_BOOK_MAP[role] || null,
    homeDashboardOrder: HOME_ORDER_MAP[role] || null,
    completionCard: completion
      ? {
          announceCopy: completion.announceCopy,
          ctaLabel: completion.ctaLabel,
          starterPrompt: completion.starterPrompt,
          destinationTab: completion.destinationTab,
        }
      : null,
  }
}
